/**
 * <gift-card-router> — forwards the gift card entry page to the
 * destination resolved server-side for the shopper's selected country.
 *
 * - Only forwards to the destination rendered by the server, and only
 *   when it is a same-origin product path.
 * - Confirms the product resolves in the current market (`<url>.js`)
 *   before forwarding; otherwise swaps in the unavailable message.
 * - Keeps the entry page's query string (minus `variant`, which belongs
 *   to a different product) and a safe fragment.
 * - Gives up after repeated forwards to the same destination in a short
 *   window, leaving the visible Continue link as the way forward.
 */
(() => {
  if (customElements.get('gift-card-router')) return;

  const LOOP_KEY = 'heirest:gift-card-forward';
  const LOOP_WINDOW_MS = 30000;
  const LOOP_MAX = 2;
  const SAFE_FRAGMENT = /^#[A-Za-z0-9_-]{1,64}$/;

  function readLoopState() {
    try {
      return JSON.parse(sessionStorage.getItem(LOOP_KEY)) || null;
    } catch (_) {
      return null;
    }
  }

  function writeLoopState(state) {
    try {
      sessionStorage.setItem(LOOP_KEY, JSON.stringify(state));
    } catch (_) {
      /* storage unavailable: forwarding still works, just unguarded */
    }
  }

  function buildTarget(destination) {
    const target = new URL(destination, window.location.origin);
    if (target.origin !== window.location.origin) return null;
    if (!/\/products\/[^/]+\/?$/.test(target.pathname)) return null;

    const params = new URLSearchParams(window.location.search);
    for (const key of [...params.keys()]) {
      if (key.toLowerCase() === 'variant') params.delete(key);
    }
    target.search = params.toString();
    target.hash = SAFE_FRAGMENT.test(window.location.hash) ? window.location.hash : '';
    return target;
  }

  class GiftCardRouter extends HTMLElement {
    connectedCallback() {
      if (this.dataset.status !== 'ready') return;

      const target = buildTarget(this.dataset.destination || '');
      if (!target) {
        this.showNote();
        return;
      }

      const link = this.querySelector('[data-gift-card-router-continue]');
      if (link) link.href = target.pathname + target.search + target.hash;

      if (this.hasAttribute('data-design-mode')) return;

      const now = Date.now();
      const previous = readLoopState();
      const sameTarget =
        previous && previous.path === target.pathname && now - previous.at < LOOP_WINDOW_MS;
      const count = sameTarget ? previous.count + 1 : 1;
      if (count > LOOP_MAX) {
        this.showNote();
        return;
      }

      this.verify(target).then((result) => {
        if (result === 'ok') {
          writeLoopState({ path: target.pathname, at: now, count });
          window.location.replace(target.href);
        } else if (result === 'missing') {
          this.showUnavailable();
        } else {
          this.showNote();
        }
      });
    }

    async verify(target) {
      try {
        const response = await fetch(`${target.pathname.replace(/\/$/, '')}.js`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });
        if (response.status === 404) return 'missing';
        if (!response.ok) return 'error';
        const product = await response.json();
        return product && product.available ? 'ok' : 'missing';
      } catch (_) {
        return 'error';
      }
    }

    showNote() {
      const note = this.querySelector('[data-gift-card-router-note]');
      if (note) note.hidden = false;
    }

    showUnavailable() {
      const template = this.querySelector('template[data-gift-card-router-unavailable]');
      const message = this.querySelector('[data-gift-card-router-message]');
      const link = this.querySelector('[data-gift-card-router-continue]');
      const contact = this.querySelector('[data-gift-card-router-contact]');
      if (template && message) message.innerHTML = template.innerHTML;
      if (link) link.hidden = true;
      if (contact) contact.hidden = false;
    }
  }

  customElements.define('gift-card-router', GiftCardRouter);
})();
